import React, { useState, useEffect } from "react";
import { Download, X, Globe, Coins, RefreshCw } from "lucide-react";
import { PDF_LANGS, PDF_CURRENCIES, refreshFxRates } from "./pdfReceipt";

/**
 * Lightweight modal to pick language + currency before PDF download.
 */
export default function PDFExportModal({ onClose, onConfirm, defaultLang = "ru", defaultCurrency = "EUR", title = "Настройки экспорта" }) {
  const [lang, setLang] = useState(defaultLang);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [downloading, setDownloading] = useState(false);
  const [fxLoaded, setFxLoaded] = useState(false);

  useEffect(() => { refreshFxRates().finally(() => setFxLoaded(true)); }, []);

  const handle = async () => {
    setDownloading(true);
    try { await onConfirm(lang, currency); }
    finally { setDownloading(false); onClose(); }
  };

  const curRate = PDF_CURRENCIES.find((c) => c.code === currency)?.rate || 1;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      data-testid="pdf-export-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, background: "rgba(11,11,16,0.98)",
          border: "1px solid var(--tg-border)", borderRadius: 16, padding: 24, color: "var(--tg-text)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>📄 {title}</h3>
          <button onClick={onClose} className="dash-icon-btn" data-testid="pdf-modal-close"><X size={16} /></button>
        </div>

        {/* Language */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <Globe size={12} /> Язык квитанции
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {PDF_LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`pdf-lang-${l.code}`}
                style={{
                  flex: 1, padding: "10px 8px", borderRadius: 10,
                  border: "1px solid " + (lang === l.code ? "var(--tg-blue)" : "var(--tg-border)"),
                  background: lang === l.code ? "rgba(0,242,255,0.12)" : "transparent",
                  color: lang === l.code ? "var(--tg-blue)" : "var(--tg-text-dim)",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >{l.label}</button>
            ))}
          </div>
        </div>

        {/* Currency grid 3x3 */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Coins size={12} /> Валюта (ECB live)
            </span>
            <span style={{ color: fxLoaded ? "#10F4A7" : "var(--tg-text-hint)", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
              <RefreshCw size={10} className={fxLoaded ? "" : "spin"} /> {fxLoaded ? "Live" : "…"}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {PDF_CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                data-testid={`pdf-currency-${c.code}`}
                style={{
                  padding: "10px 6px", borderRadius: 10,
                  border: "1px solid " + (currency === c.code ? "#FFD700" : "var(--tg-border)"),
                  background: currency === c.code ? "rgba(255,215,0,0.1)" : "transparent",
                  color: currency === c.code ? "#FFD700" : "var(--tg-text-dim)",
                  fontWeight: 700, fontSize: 12, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                }}
              >{c.symbol} {c.code}</button>
            ))}
          </div>
          {currency !== "EUR" && (
            <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 10 }}>
              Курс ECB: 1 EUR = <b style={{ color: "#FFD700" }}>{curRate.toFixed(curRate > 10 ? 2 : 4)} {currency}</b>
            </div>
          )}
        </div>

        <button
          onClick={handle}
          disabled={downloading}
          className="tg-btn gold"
          data-testid="pdf-modal-confirm"
          style={{ width: "100%", padding: "12px", fontSize: 14, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: downloading ? 0.6 : 1 }}
        >
          <Download size={16} /> {downloading ? "Генерация…" : "Скачать PDF"}
        </button>
      </div>
    </div>
  );
}
